import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Plus, Trash2, Target, ShieldCheck, TrendingUp, Lock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useUI } from "@/context/UIContext";
import { SiteHeader } from "@/components/SiteHeader";
import { Footer } from "@/components/BusinessSection";
import { SYSTEM_STYLES, SPECIES_ICON } from "@/lib/stateMeta";
import { PlannerExplorer } from "@/components/PlannerExplorer";

const TIER = {
  strong: { label: "Draw this year", cls: "bg-[#2E7D46]/12 text-[#2E7D46] border-[#2E7D46]/30" },
  possible: { label: "Realistic", cls: "bg-[#B7761F]/12 text-[#B7761F] border-[#B7761F]/30" },
  longshot: { label: "Long shot", cls: "bg-[#5C6B61]/10 text-[#5C6B61] border-[#5C6B61]/25" },
  unknown: { label: "Unknown", cls: "bg-[#5C6B61]/10 text-[#5C6B61] border-[#5C6B61]/25" },
};

export default function Planner() {
  const { isAuthed, ready } = useAuth();
  const { openAuth } = useUI();
  const [states, setStates] = useState([]);
  const [plan, setPlan] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ state: "", species: "", points: "" });
  const [speciesOpts, setSpeciesOpts] = useState([]);

  useEffect(() => { api.exploreStates().then(setStates).catch(() => {}); }, []);
  useEffect(() => { if (isAuthed) load(); }, [isAuthed]);

  const load = () => api.drawPlan().then((d) => setPlan(d.plans)).catch(() => setPlan([]));

  // Species options for the chosen state (only states with verified data expose species)
  useEffect(() => {
    setForm((f) => ({ ...f, species: "" }));
    setSpeciesOpts([]);
    if (form.state) api.exploreSpecies(form.state).then((d) => setSpeciesOpts(d.species || [])).catch(() => {});
  }, [form.state]);

  const covered = useMemo(
    () => [...states].sort((a, b) => (b.has_data - a.has_data) || a.state.localeCompare(b.state)),
    [states]);

  const save = async () => {
    if (!form.state || !form.species || form.points === "") { toast.error("Pick a state, species and your points."); return; }
    setSaving(true);
    try {
      await api.savePoints({ state: form.state, species: form.species, points: Number(form.points) });
      toast.success(`Saved ${form.species} points for ${form.state}`);
      setForm({ state: "", species: "", points: "" });
      await load();
    } catch { toast.error("Could not save. Try again."); }
    setSaving(false);
  };

  const remove = async (id) => { await api.deletePoints(id); await load(); };

  const inp = "border border-saddle/25 bg-white px-3 py-2.5 text-sm outline-none focus:border-forest w-full";

  return (
    <div className="bg-paper min-h-screen" data-testid="planner-page">
      <SiteHeader />
      <section className="bg-forest text-bone px-5 sm:px-10 pt-14 pb-12 grain relative overflow-hidden">
        <div className="max-w-[1200px] mx-auto relative z-10">
          <span className="hr-overline text-blaze inline-flex items-center gap-2"><Target size={14} /> Points Planner</span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[0.9] tracking-tight mt-3">Your draw plan.</h1>
          <p className="text-bone/75 mt-4 max-w-2xl text-base">Save your point balance for each state &amp; species. We&apos;ll show which real, verified hunts you can realistically draw this year — personalized to your points.</p>
        </div>
      </section>

      <main className="px-5 sm:px-10 py-12 max-w-[1200px] mx-auto">
        {!ready ? (
          <div className="py-24 grid place-items-center"><Loader2 className="animate-spin text-forest" /></div>
        ) : !isAuthed ? (
          <div className="max-w-lg mx-auto text-center py-16" data-testid="planner-auth-gate">
            <span className="w-14 h-14 rounded-full bg-forest/10 grid place-items-center mx-auto"><Lock className="text-forest" /></span>
            <h2 className="font-serif text-3xl text-forest mt-5">Sign in to build your plan</h2>
            <p className="text-charcoal/65 mt-3">Your points are saved to your HomeRange account so your draw plan is ready whenever you are.</p>
            <button onClick={() => openAuth("login")} className="mt-7 bg-blaze text-white px-6 py-3 text-sm font-semibold uppercase tracking-wider hover:bg-blaze-dark transition-colors" data-testid="planner-signin-btn">Sign in / Create account</button>
          </div>
        ) : (
          <div className="space-y-12">
            {/* Guided explorer — the primary experience (blueprint §18) */}
            <PlannerExplorer />

            <div className="border-t border-saddle/15" />

            {/* Add points (saved balances — powers the dashboard Point Wallet) */}
            <section data-testid="planner-add">
              <h2 className="font-serif text-2xl sm:text-3xl text-forest tracking-tight">Your saved point balances</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-5 bg-white border border-saddle/20 p-4">
                <select value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className={inp} data-testid="planner-state">
                  <option value="">Select state…</option>
                  {covered.map((s) => <option key={s.state} value={s.state}>{s.state}{s.has_data ? "" : " (coverage in progress)"}</option>)}
                </select>
                <select value={form.species} onChange={(e) => setForm({ ...form, species: e.target.value })} className={inp} data-testid="planner-species" disabled={!speciesOpts.length}>
                  <option value="">{form.state && !speciesOpts.length ? "No verified species yet" : "Select species…"}</option>
                  {speciesOpts.map((s) => <option key={s.species} value={s.species}>{s.species}</option>)}
                </select>
                <input type="number" min="0" value={form.points} onChange={(e) => setForm({ ...form, points: e.target.value })} placeholder="Your points" className={inp} data-testid="planner-points" />
                <button onClick={save} disabled={saving} className="bg-forest text-bone font-semibold uppercase tracking-wider text-sm px-4 py-2.5 inline-flex items-center justify-center gap-2 hover:bg-blaze transition-colors disabled:opacity-60" data-testid="planner-save">
                  {saving ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Save points
                </button>
              </div>
              <p className="text-xs text-charcoal/50 mt-2">Only states with verified draw data return hunt predictions. Personalized odds are transparent estimates, not a guarantee.</p>
            </section>

            {/* Plans */}
            {plan === null ? (
              <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-forest" /></div>
            ) : plan.length === 0 ? (
              <div className="text-center py-16 border border-dashed border-saddle/30" data-testid="planner-empty">
                <p className="font-serif text-2xl text-forest">No points saved yet</p>
                <p className="text-charcoal/60 mt-2">Add your first state &amp; species above to see your draw plan.</p>
              </div>
            ) : (
              <div className="space-y-8" data-testid="planner-plans">
                {plan.map((p) => <PlanCard key={p.id} p={p} onRemove={remove} />)}
              </div>
            )}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

function PlanCard({ p, onRemove }) {
  const sysCls = SYSTEM_STYLES[p.system?.system] || SYSTEM_STYLES.otc_general;
  return (
    <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-white border border-saddle/20" data-testid={`plan-${p.id}`}>
      <div className="flex items-start justify-between gap-4 p-5 border-b border-saddle/12">
        <div className="flex items-center gap-4">
          <span className="w-12 h-12 rounded-full bg-forest grid place-items-center overflow-hidden shrink-0">
            {SPECIES_ICON[p.species] ? <img src={SPECIES_ICON[p.species]} alt={p.species} className="w-10 h-10 object-contain" loading="lazy" /> : <Target className="text-blaze" size={18} />}
          </span>
          <div>
            <h3 className="font-serif text-2xl text-forest leading-none">{p.state} · {p.species}</h3>
            <div className="flex items-center gap-3 mt-2">
              <span className={`text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 ${sysCls}`}>{p.system?.system_label}</span>
              <span className="text-sm text-charcoal/60"><b className="text-forest tabular-nums">{p.points}</b> points</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {p.has_data && (
            <span className="text-right hidden sm:block">
              <span className="block font-serif text-2xl text-blaze tabular-nums leading-none">{p.drawable_count}</span>
              <span className="text-[10px] uppercase tracking-wide text-charcoal/45">can draw now</span>
            </span>
          )}
          <button onClick={() => onRemove(p.id)} className="text-charcoal/40 hover:text-[#B04A3A] transition-colors p-2" data-testid={`plan-remove-${p.id}`} aria-label="Remove"><Trash2 size={16} /></button>
        </div>
      </div>

      {!p.has_data ? (
        <p className="px-5 py-6 text-charcoal/60 text-sm" data-testid={`plan-nodata-${p.id}`}>
          Verified {p.state} {p.species} draw data is being collected. We only show numbers confirmed against official state sources — check back soon.
        </p>
      ) : (
        <div className="divide-y divide-saddle/10" data-testid={`plan-hunts-${p.id}`}>
          {p.hunts.map((h) => {
            const tier = TIER[h.tier?.key] || TIER.unknown;
            const est = h.personalized?.est_odds_pct;
            return (
              <div key={h.id} className="flex items-center justify-between gap-4 px-5 py-3.5" data-testid={`plan-hunt-${h.id}`}>
                <div className="min-w-0">
                  <p className="font-serif text-lg text-forest truncate">{h.unit}</p>
                  <p className="text-xs text-charcoal/55">{h.weapon} · {h.residency}{h.season ? ` · ${h.season}` : ""} · state odds {h.draw_odds_pct}%</p>
                  {h.creep?.applicable ? (
                    <p className="text-[11px] text-charcoal/50 mt-1 flex flex-wrap items-center gap-x-2" data-testid={`plan-creep-${h.id}`}>
                      <TrendingUp size={11} className="text-saddle" />
                      <span>Point creep {h.creep.avg_annual_creep != null ? `+${h.creep.avg_annual_creep}/yr` : "—"}</span>
                      <span className="text-charcoal/30">·</span>
                      <span>{h.creep.trend?.label}</span>
                      {h.creep.projection && (
                        <>
                          <span className="text-charcoal/30">·</span>
                          <span title={h.creep.projection.label}>Next yr est. {h.creep.projection.next_year_low}{h.creep.projection.next_year_high !== h.creep.projection.next_year_low ? `–${h.creep.projection.next_year_high}` : ""} pts*</span>
                        </>
                      )}
                    </p>
                  ) : h.point_system_type && !["none", "random"].includes(h.point_system_type) ? (
                    <p className="text-[11px] text-charcoal/50 mt-1" data-testid={`plan-bonus-${h.id}`}>Bonus points improve your odds — no guaranteed threshold.</p>
                  ) : null}
                </div>
                <div className="flex items-center gap-4 shrink-0">
                  <div className="text-right">
                    <span className="block font-serif text-xl text-blaze tabular-nums leading-none">{est != null ? `${est}%` : "—"}</span>
                    <span className="text-[10px] uppercase tracking-wide text-charcoal/45">your odds</span>
                  </div>
                  <span className={`text-[11px] font-semibold uppercase tracking-wide px-2.5 py-1 border ${tier.cls}`} data-testid={`plan-tier-${h.id}`}>{h.tier?.label}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {p.has_data && (
        <p className="px-5 py-3 text-[11px] text-charcoal/45 flex items-center gap-1.5 border-t border-saddle/12">
          <ShieldCheck size={12} className="text-[#2E7D46]" /> Personalized from verified state data + point creep <TrendingUp size={12} className="text-saddle ml-1" /> — a transparent estimate, not a guarantee. *Next-year point figures are HomeRange Projections, not official draw results.
        </p>
      )}
    </motion.section>
  );
}
